import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { env } from '@/config/env'
import {
  fetchPublicTenantBranding,
  tenantBrandingString,
  type TenantBrandingRecord,
} from '@/services/api/tenant-branding'
import type { TenantPublicEInvoicePolicy } from '@/services/api/tenant-public-settings-api'
import {
  acknowledgeCpAddonCodesInStorage,
  computePendingCpAddonCelebrationCodes,
  purchasableAddonCodesFromCpEntitlements,
} from '@/utils/cp-addon-celebration-storage'
import { applyTenantPwaBranding } from '@/utils/pwa-branding'
import { applyAdminSeoMeta, buildAdminDocumentTitle } from '@/utils/admin-seo'
import { canonicalBrandingFontStack } from '@/utils/branding-font-policy'

const BRANDING_CACHE_KEY_V1 = 'tb.tenantBranding.v1'

function tenantBrandingCacheStorageKey(): string {
  return `${BRANDING_CACHE_KEY_V1}:${env.apiUrl}`
}

const HEX_6 = /^#?([0-9a-f]{6})$/i
const HEX_3 = /^#?([0-9a-f]{3})$/i

function expandHex3(short: string): string {
  const [a, b, c] = short.split('')
  return `${a}${a}${b}${b}${c}${c}`
}

/**
 * Parses #RGB or #RRGGBB into [r, g, b] or null.
 */
function parseHexRgb(hex: string): [number, number, number] | null {
  const t = hex.trim()
  let m = HEX_6.exec(t)
  if (m) {
    const n = parseInt(m[1], 16)
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
  }
  m = HEX_3.exec(t)
  if (m) {
    const full = expandHex3(m[1])
    const n = parseInt(full, 16)
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
  }
  return null
}

function rgbTripletString(rgb: [number, number, number]): string {
  return `${rgb[0]} ${rgb[1]} ${rgb[2]}`
}

/**
 * Darkens RGB channels by mixing toward black (0–1 factor of remaining light).
 */
function shadeTowardBlack(rgb: [number, number, number], factor: number): [number, number, number] {
  const f = Math.max(0, Math.min(1, factor))
  return rgb.map((c) => Math.round(c * f)) as [number, number, number]
}

/**
 * Lightens RGB channels by mixing toward white.
 * factor = 0 → white, factor = 1 → original color
 */
function shadeTowardWhite(rgb: [number, number, number], factor: number): [number, number, number] {
  const f = Math.max(0, Math.min(1, factor))
  return rgb.map((c) => Math.round(c * f + 255 * (1 - f))) as [number, number, number]
}

function applyCssBrandFromPrimary(root: HTMLElement, primaryHex: string): [number, number, number] {
  const parsed = parseHexRgb(primaryHex)
  /* Default indigo aligns with subscription-dashboard static brand */
  const fallback: [number, number, number] = [99, 102, 241]
  const base = parsed ?? fallback
  root.style.setProperty('--tenant-brand-300', rgbTripletString(shadeTowardWhite(base, 0.55)))
  root.style.setProperty('--tenant-brand-400', rgbTripletString(shadeTowardWhite(base, 0.75)))
  root.style.setProperty('--tenant-brand-500', rgbTripletString(base))
  root.style.setProperty('--tenant-brand-600', rgbTripletString(shadeTowardBlack(base, 0.88)))
  root.style.setProperty('--tenant-brand-700', rgbTripletString(shadeTowardBlack(base, 0.75)))
  root.style.setProperty('--brand-primary', rgbTripletString(base))
  root.style.setProperty('--brand-primary-rgb', `${base[0]}, ${base[1]}, ${base[2]}`)
  return base
}

/**
 * Accent stripe / secondary UI (falls back to primary when accentColor unset or invalid).
 */
function applyAccentCss(root: HTMLElement, b: TenantBrandingRecord, primaryRgb: [number, number, number]): void {
  const accentHex = tenantBrandingString(b, 'accentColor').trim()
  const accentParsed = accentHex ? parseHexRgb(accentHex) : null
  const accentRgb = accentParsed ?? primaryRgb
  root.style.setProperty('--tenant-accent-500', rgbTripletString(accentRgb))
  root.style.setProperty('--tenant-accent-600', rgbTripletString(shadeTowardBlack(accentRgb, 0.88)))
}

function applyBrandingToDocument(b: TenantBrandingRecord): void {
  const root = document.documentElement
  const primary = tenantBrandingString(b, 'primaryColor').trim() || '#6366f1'
  const primaryRgb = applyCssBrandFromPrimary(root, primary)
  applyAccentCss(root, b, primaryRgb)

  // document.title is managed by router (syncDocumentTitle) so it can include the current page.
  applyAdminSeoMeta(b, document.title || buildAdminDocumentTitle(b))

  const fav = tenantBrandingString(b, 'faviconUrl').trim()
  if (fav) {
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.head.appendChild(link)
    }
    link.href = fav
  }
  applyTenantPwaBranding(b)

  const sans = canonicalBrandingFontStack(tenantBrandingString(b, 'fontFamilySans'))
  if (sans) {
    root.style.setProperty('--tenant-font-sans', sans)
  } else {
    root.style.removeProperty('--tenant-font-sans')
  }

  const heading = canonicalBrandingFontStack(tenantBrandingString(b, 'fontFamilyHeading'))
  if (heading) {
    root.style.setProperty('--tenant-font-heading', heading)
  } else {
    root.style.removeProperty('--tenant-font-heading')
  }
}

export const useTenantBrandingStore = defineStore('tenantBranding', () => {
  const branding = ref<TenantBrandingRecord>({})
  const brandingRevision = ref(0)
  const cpEntitlements = ref<string[]>([])
  const cpPurchasedAddOns = ref<string[]>([])
  const cpDisabledAddOns = ref<string[]>([])
  const eInvoicePolicy = ref<TenantPublicEInvoicePolicy | null>(null)
  const activeEntitlements = computed(() => {
    const disabled = new Set(cpDisabledAddOns.value.map((code) => code.trim()).filter(Boolean))
    return cpEntitlements.value.filter((entitlement) => {
      if (!entitlement.startsWith('addon:')) return true
      return !disabled.has(entitlement.slice('addon:'.length))
    })
  })
  const seatingProActive = computed(
    () => activeEntitlements.value.includes('addon:seating_pro'),
  )
  const giftCardsActive = computed(
    () => activeEntitlements.value.includes('addon:gift_cards'),
  )
  const vendorSystemActive = computed(
    () => activeEntitlements.value.includes('addon:vendor_system'),
  )
  const analyticsActive = computed(
    () => activeEntitlements.value.includes('addon:analytics'),
  )
  const ticketBuddyConnectActive = computed(
    () => activeEntitlements.value.includes('addon:ticket_buddy_connect'),
  )
  const hardTicketsActive = computed(
    () => activeEntitlements.value.includes('addon:hard_tickets'),
  )
  const marketingSuiteActive = computed(
    () => activeEntitlements.value.includes('addon:marketing_suite'),
  )
  const experienceProviderActive = computed(
    () => activeEntitlements.value.includes('addon:experience_provider'),
  )
  const loaded = ref(false)
  /** Purchasable CP add-on codes (no `addon:` prefix) pending celebration UI after network branding fetch. */
  const pendingCpAddonCelebration = ref<string[]>([])

  function refreshPendingCpAddonCelebration(nextEnt: string[]): void {
    const purchasable = purchasableAddonCodesFromCpEntitlements(nextEnt)
    pendingCpAddonCelebration.value = computePendingCpAddonCelebrationCodes(purchasable)
  }

  function dismissCpAddonCelebration(): void {
    const codes = pendingCpAddonCelebration.value
    if (codes.length === 0) {
      return
    }
    acknowledgeCpAddonCodesInStorage(codes)
    pendingCpAddonCelebration.value = []
  }

  /** Coalesce concurrent load() calls (e.g. bootstrap + route guards). */
  let loadInflight: Promise<void> | null = null

  async function runLoad(): Promise<void> {
    try {
      const payload = await fetchPublicTenantBranding()
      const b = payload.branding
      const rev = payload.brandingRevision
      let nextEnt = Array.isArray(payload.cpEntitlements) ? payload.cpEntitlements.map(String) : []
      const nextDisabled = Array.isArray(payload.cpDisabledAddOns) ? payload.cpDisabledAddOns.map(String) : []
      // Keep store entitlements aligned with API booleans if the array ever omits addon codes.
      if (payload.seatingProActive === true && !nextDisabled.includes('seating_pro') && !nextEnt.includes('addon:seating_pro')) {
        nextEnt = [...nextEnt, 'addon:seating_pro']
      }
      if (payload.giftCardsActive === true && !nextDisabled.includes('gift_cards') && !nextEnt.includes('addon:gift_cards')) {
        nextEnt = [...nextEnt, 'addon:gift_cards']
      }
      if (payload.vendorSystemActive === true && !nextDisabled.includes('vendor_system') && !nextEnt.includes('addon:vendor_system')) {
        nextEnt = [...nextEnt, 'addon:vendor_system']
      }
      if (payload.mobileAppActive === true && !nextDisabled.includes('mobile_app') && !nextEnt.includes('addon:mobile_app')) {
        nextEnt = [...nextEnt, 'addon:mobile_app']
      }
      if (payload.analyticsActive === true && !nextDisabled.includes('analytics') && !nextEnt.includes('addon:analytics')) {
        nextEnt = [...nextEnt, 'addon:analytics']
      }
      if (payload.aiWebshopAssistantActive === true && !nextDisabled.includes('ai_webshop_assistant') && !nextEnt.includes('addon:ai_webshop_assistant')) {
        nextEnt = [...nextEnt, 'addon:ai_webshop_assistant']
      }
      if (payload.hardTicketsActive === true && !nextDisabled.includes('hard_tickets') && !nextEnt.includes('addon:hard_tickets')) {
        nextEnt = [...nextEnt, 'addon:hard_tickets']
      }
      if (payload.marketingSuiteActive === true && !nextDisabled.includes('marketing_suite') && !nextEnt.includes('addon:marketing_suite')) {
        nextEnt = [...nextEnt, 'addon:marketing_suite']
      }
      if (payload.experienceProviderActive === true && !nextDisabled.includes('experience_provider') && !nextEnt.includes('addon:experience_provider')) {
        nextEnt = [...nextEnt, 'addon:experience_provider']
      }
      branding.value = b
      brandingRevision.value = rev
      cpEntitlements.value = nextEnt
      cpPurchasedAddOns.value = Array.isArray(payload.cpPurchasedAddOns) ? payload.cpPurchasedAddOns.map(String) : []
      cpDisabledAddOns.value = nextDisabled
      eInvoicePolicy.value = payload.eInvoicePolicy ?? null
      applyBrandingToDocument(b)
      refreshPendingCpAddonCelebration(nextEnt)
      try {
        localStorage.setItem(
          tenantBrandingCacheStorageKey(),
          JSON.stringify({
            branding: b,
            brandingRevision: rev,
            cpEntitlements: nextEnt,
            cpPurchasedAddOns: cpPurchasedAddOns.value,
            cpDisabledAddOns: cpDisabledAddOns.value,
            savedAt: Date.now(),
          }),
        )
      } catch {
        /* ignore quota / private mode */
      }
    } catch {
      eInvoicePolicy.value = null
      try {
        const raw = localStorage.getItem(tenantBrandingCacheStorageKey())
        if (raw) {
          const parsed = JSON.parse(raw) as {
            branding?: TenantBrandingRecord
            brandingRevision?: number
            cpEntitlements?: unknown
            cpPurchasedAddOns?: unknown
            cpDisabledAddOns?: unknown
          }
          if (parsed.branding && typeof parsed.branding === 'object' && !Array.isArray(parsed.branding)) {
            branding.value = parsed.branding
            const r = Number(parsed.brandingRevision)
            brandingRevision.value = Number.isFinite(r) ? r : 0
            const ce = parsed.cpEntitlements
            cpEntitlements.value = Array.isArray(ce) ? ce.map((x) => String(x)) : []
            const purchased = parsed.cpPurchasedAddOns
            cpPurchasedAddOns.value = Array.isArray(purchased) ? purchased.map((x) => String(x)) : []
            const disabled = parsed.cpDisabledAddOns
            cpDisabledAddOns.value = Array.isArray(disabled) ? disabled.map((x) => String(x)) : []
            applyBrandingToDocument(parsed.branding)
            return
          }
        }
      } catch {
        /* ignore corrupt cache */
      }
      branding.value = {}
      brandingRevision.value = 0
      cpEntitlements.value = []
      cpPurchasedAddOns.value = []
      cpDisabledAddOns.value = []
      eInvoicePolicy.value = null
    } finally {
      loaded.value = true
    }
  }

  async function load(): Promise<void> {
    if (loadInflight !== null) {
      return loadInflight
    }
    loadInflight = runLoad()
    try {
      await loadInflight
    } finally {
      loadInflight = null
    }
  }

  return {
    branding,
    brandingRevision,
    cpEntitlements,
    activeEntitlements,
    cpPurchasedAddOns,
    cpDisabledAddOns,
    eInvoicePolicy,
    seatingProActive,
    giftCardsActive,
    vendorSystemActive,
    analyticsActive,
    ticketBuddyConnectActive,
    hardTicketsActive,
    marketingSuiteActive,
    experienceProviderActive,
    loaded,
    load,
    pendingCpAddonCelebration,
    dismissCpAddonCelebration,
  }
})
